import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import type { ExtensionListItem } from '@kirin/api-client/contracts/extensions';
import { queryKeys } from '@/hooks/api/queryKeys';
import { useCanViewAdminContent } from '@/hooks/useRoleCheck';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { createOrgExtensionColumns } from './columns';
import { fetchOrgExtensions } from './api';
import { sanitizeOrgExtensionParams } from './sanitizeOrgExtensionParams';
import { ORG_EXTENSION_TABLE_CONFIG } from './constants';
import { ExtensionUserListModal } from '@/pages/organization/components/ExtensionUserListModal';
import { ExtensionVersionsTable } from './ExtensionVersionsTable';
import {
  AgentmeshRiskDetails,
  readsAsBuiltIn,
} from '@/components/shared/extensions';

/**
 * DataTable-based extensions tab for the organization dashboard.
 * Shows org extensions with filtering, sorting, and search.
 *
 * organizationId is passed as a prop so the columns closure always
 * captures the correct org — no stale ID from a previous render.
 */
export function DataTableOrgExtensionsContent({
  organizationId,
}: {
  organizationId: string;
}): ReactElement {
  const [selectedExtension, setSelectedExtension] =
    useState<ExtensionListItem | null>(null);
  // KIR-5281: set when the modal was opened from a version row's user count.
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  const handleUserClick = useCallback(
    (extension: ExtensionListItem, version?: string) => {
      setSelectedExtension(extension);
      setSelectedVersion(version ?? null);
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setSelectedExtension(null);
    setSelectedVersion(null);
  }, []);

  // The Users filter sources its options from the org members list, which is
  // RBAC-gated to owner/manager/support. Only offer the filter to viewers who
  // can load it; members get a non-filterable Users column and never fire the
  // members fetch (→ 403). KIR-3266.
  const { canViewAdminContent, isLoading: roleLoading } =
    useCanViewAdminContent(organizationId);
  const canFilterByUser = !roleLoading && canViewAdminContent;
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.CAPABILITY_DETECTION,
    flagsmithFeatureFlags.AGENTMESH_RISK_ESTIMATE,
  ]);
  const capabilityDetectionEnabled =
    flags[flagsmithFeatureFlags.CAPABILITY_DETECTION] ?? false;
  const agentmeshRiskEnabled =
    flags[flagsmithFeatureFlags.AGENTMESH_RISK_ESTIMATE] ?? false;

  const columns = useMemo(
    () =>
      createOrgExtensionColumns(
        organizationId,
        handleUserClick,
        canFilterByUser,
        capabilityDetectionEnabled,
        agentmeshRiskEnabled
      ),
    [
      organizationId,
      handleUserClick,
      canFilterByUser,
      capabilityDetectionEnabled,
      agentmeshRiskEnabled,
    ]
  );

  // Drop a stale `user` filter from URL state for members before it reaches the
  // query key or the request — the column gate alone doesn't strip a filter
  // rehydrated from a prior owner/manager session (KIR-3266 CR). Mirrors the
  // org-alerts tab's sanitizeAlertsParams.
  const sanitizeParams = useCallback(
    (params: DataFetchParams): DataFetchParams =>
      sanitizeOrgExtensionParams(params, canFilterByUser),
    [canFilterByUser]
  );

  const getQueryKeysFromParams = useCallback(
    (params: DataFetchParams) => {
      const sanitized = sanitizeParams(params);
      return queryKeys.extensions.orgDataTable(organizationId, {
        page: sanitized.pagination.pageIndex + 1,
        limit: sanitized.pagination.pageSize,
        filters: sanitized.filters,
        sortBy: sanitized.sort?.field,
        sortOrder: sanitized.sort?.order,
        search: sanitized.search,
      });
    },
    [organizationId, sanitizeParams]
  );

  const renderExpandedContent = useCallback(
    (extension: ExtensionListItem) => (
      <>
        {agentmeshRiskEnabled ? (
          <AgentmeshRiskDetails
            riskEstimate={extension.riskEstimate}
            isBuiltIn={readsAsBuiltIn(extension)}
          />
        ) : null}
        <ExtensionVersionsTable
          versions={extension.versions}
          onUserClick={version => handleUserClick(extension, version)}
          organizationId={organizationId}
          extensionId={extension.extensionId}
          capabilityDetectionEnabled={capabilityDetectionEnabled}
          agentmeshRiskEnabled={agentmeshRiskEnabled}
          isBuiltIn={extension.isBuiltIn}
        />
      </>
    ),
    [
      agentmeshRiskEnabled,
      capabilityDetectionEnabled,
      handleUserClick,
      organizationId,
    ]
  );

  return (
    <>
      <DataTable<ExtensionListItem>
        id={ORG_EXTENSION_TABLE_CONFIG.tableId}
        mode="server"
        className="flex flex-col flex-1 min-h-0"
        containerClassName="flex-1"
        columns={columns}
        errorBoundaryLabel="Something went wrong in the extensions table"
        search={{
          placeholder: 'Search extensions...',
          className: 'w-[260px]',
        }}
        features={{ enableSorting: true, enableRowExpansion: true }}
        rowExpansion={{
          render: renderExpandedContent,
          getRowId: row => row.extensionId,
          expandOnRowClick: true, // Match legacy: clicking row expands it
          showContainer: false, // Match legacy: no container styling
          contentClassName: '',
        }}
        queryFn={(params, signal) =>
          fetchOrgExtensions(organizationId, sanitizeParams(params), signal)
        }
        getQueryKeysFromParams={getQueryKeysFromParams}
        noDataLabel="No extensions found for this organization."
        countScope={{ organizationId }}
        queryConfig={{ staleTime: 30000, refetchOnWindowFocus: false }}
      />
      <ExtensionUserListModal
        organizationId={organizationId}
        extensionId={selectedExtension?.extensionId ?? null}
        extensionDisplayName={
          selectedExtension?.displayName ?? selectedExtension?.extensionName
        }
        version={
          selectedExtension && selectedVersion !== null
            ? {
                publisher: selectedExtension.publisher,
                version: selectedVersion,
              }
            : undefined
        }
        onClose={handleCloseModal}
      />
    </>
  );
}

DataTableOrgExtensionsContent.displayName = 'DataTableOrgExtensionsContent';

export default function DataTableOrgExtensionsTab({
  organizationId,
}: {
  organizationId: string;
}): ReactElement {
  return <DataTableOrgExtensionsContent organizationId={organizationId} />;
}
